import {
  alpha,
  Badge,
  Box,
  Divider,
  Button,
  List,
  ListItem,
  Popover,
  Tooltip,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
  Avatar,
  Fade,
  CircularProgress,
  Stack
} from '@mui/material';
import { useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootStateType } from 'src/store/RootReducer';
import { styled, useTheme } from '@mui/material/styles';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import CloseIcon from '@mui/icons-material/Close';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { toast } from 'react-toastify';
import {
  getLeaveNotificationDetails,
  updateLeaveNotificationDetails
} from '../../../../../services/notificationService';
import { Notification } from '../../../../../models/notificationTypes';
import useResponsiveFontSize from 'src/hooks/useResponsiveFontSize';
import useResponsiveBreakpoints  from 'src/hooks/useResponsiveBreakpoints';

// Styled components
const AppleCard = styled(Box)(({ theme }) => ({
  background: alpha(theme.palette.background.paper, 0.6),
  backdropFilter: 'blur(18px) saturate(1.5)',
  borderRadius: 2,
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.14)',
  border: `1px solid ${alpha(theme.palette.divider, 0.17)}`,
  overflow: 'hidden'
}));

const AppleBadge = styled(Badge)(({ theme }) => ({
  '.MuiBadge-badge': {
    background: 'linear-gradient(90deg, #ff5f45, #ffb041)',
    color: '#fff',
    minWidth: 17,
    height: 17,
    fontWeight: 700,
    fontSize: '0.7rem',
    boxShadow: `0 0 0 2px ${alpha(theme.palette.background.paper, 0.8)}`,
    border: `2px solid ${theme.palette.background.paper}`,
    animation: 'pulse 1.2s infinite ease-in-out'
  },
  '@keyframes pulse': {
    '0%': { boxShadow: `0 0 0 0 ${alpha('#ff5f45', 0.7)}` },
    '100%': { boxShadow: `0 0 0 8px ${alpha('#ff5f45', 0)}` }
  }
}));

const getInitials = (title: string) =>
  title
    ? title
        .split(' ')
        .map((w) => w[0]?.toUpperCase())
        .join('')
        .substring(0, 2)
    : '?';

const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const MAX_VISIBLE = 3; // max notifications to show by default


const HeaderNotifications = () => {
  const theme = useTheme();
  // Responsive breakpoints
  const isXs = useResponsiveBreakpoints().isXs;
  const isSm = useResponsiveBreakpoints().isSm;
  const isMd = useResponsiveBreakpoints().isMd;
  const isLg = useResponsiveBreakpoints().isLg;
  const isXl = useResponsiveBreakpoints().isXl;
  const isXxl = useResponsiveBreakpoints().isXxl;
  const fontSize = useResponsiveFontSize();

  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [viewAll, setViewAll] = useState(false);

  const employeeId = useSelector(
    (state: RootStateType) => state.appReducer?.user?.EmployeeId
  );

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await getLeaveNotificationDetails(employeeId, true);
      setNotifications(data);
    } catch (error) {
      setNotifications([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  const handleNotificationClick = async (notif: Notification) => {
    setSelectedNotification(notif);
    setDetailOpen(true);
    if (notif.status === 'PENDING') {
      try {
        await updateLeaveNotificationDetails([
          { id: notif.id, status: 'VIEWED' }
        ]);
        setNotifications((prev) =>
          prev.map((n) => (n.id === notif.id ? { ...n, status: 'VIEWED' } : n))
        );
      } catch (err) {
        toast.error('Error updating notification status');
      }
    }
  };

  const handleNotificationClear = async () => {
    const payload = notifications.map((notif) => ({
      id: notif.id,
      status: 'CANCELED'
    }));

    if (payload.length === 0) return;

    try {
      await updateLeaveNotificationDetails(payload);
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, status: 'CANCELED' }))
      );
      fetchData();
    } catch (err) {
      toast.error('Error clearing notifications');
    }
  };

  // Balanced Popover/dialog width for XL/XXL screens
  let popoverWidth;
  if (isXs) popoverWidth = '96vw';
  else if (isSm) popoverWidth = 340;
  else if (isMd) popoverWidth = 360;
  else if (isLg) popoverWidth = 380;
  else if (isXxl) popoverWidth = 520;
  else if (isXl) popoverWidth = 400;
  else popoverWidth = 360;

  let popoverMaxHeight;
  if (isXs) popoverMaxHeight = 320;
  else if (isSm) popoverMaxHeight = 380;
  else if (isMd) popoverMaxHeight = 440;
  else if (isLg) popoverMaxHeight = 540;
  else if (isXxl) popoverMaxHeight = 600;
  else if (isXl) popoverMaxHeight = 480;
  else popoverMaxHeight = 440;

  let detailDialogWidth;
  if (isXs) detailDialogWidth = '96vw';
  else if (isSm) detailDialogWidth = 320;
  else if (isMd) detailDialogWidth = 340;
  else if (isLg) detailDialogWidth = 360;
  else if (isXxl) detailDialogWidth = 520;
  else if (isXl) detailDialogWidth = 400;
  else detailDialogWidth = 340;

  // Render list items based on viewAll state
  const visibleNotifications = viewAll
    ? notifications
    : notifications.slice(0, MAX_VISIBLE);

  const renderNotificationItem = (notif: Notification) => (
    <ListItem
      key={notif.id}
      sx={{
        py: { xs: 1, sm: 1.1, md: 1.2, lg: 1.3, xl: 1.1 },
        px: { xs: 1, sm: 1.1, md: 1.2, lg: 1.3, xl: 1.1 },
        mb: 1.2,
        borderRadius: 2,
        background: notif.status === 'PENDING'
          ? 'linear-gradient(92deg, rgba(248,249,255,1) 70%, rgba(255,239,233,0.95) 100%)'
          : alpha('#fff', 0.72),
        boxShadow: notif.status === 'PENDING'
          ? '0 2px 10px 0 rgba(85, 105, 255, 0.05)'
          : undefined,
        border: notif.status === 'PENDING'
          ? '1px solid #e6e6f8'
          : '1px solid #f2f2f2',
        cursor: 'pointer',
        transition: 'background 0.22s, box-shadow 0.22s',
        '&:hover': {
          background: 'linear-gradient(90deg, #f0f5ff 70%, #fff6ed 100%)',
          boxShadow: '0 4px 18px 0 rgba(31,38,135,0.04)'
        },
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        minWidth: 0,
        maxWidth: '100%'
      }}
      onClick={() => handleNotificationClick(notif)}
      aria-label={`Notification: ${notif.title}`}
    >
      <Box
        sx={{
          position: 'relative',
          mr: 1.2,
          mt: .3,
          flexShrink: 0
        }}>
        <Avatar
          sx={{
            width: { xs: 30, sm: 32, md: 34, lg: 36, xl: 38, xxl: 40 },
            height: { xs: 30, sm: 32, md: 34, lg: 36, xl: 38, xxl: 40 },
            fontWeight: 700,
            fontSize: { xs: 15, sm: 16, md: 17, lg: 18, xl: 20, xxl: 22 },
            bgcolor: notif.status === 'PENDING' ? '#fff' : '#f6f6fa',
            color: notif.status === 'PENDING' ? '#262626' : '#bdbdbd',
            border: notif.status === 'PENDING'
              ? '1.5px solid #5569ff'
              : '1px solid #ececec',
            boxShadow: notif.status === 'PENDING'
              ? '0 1px 6px 0 rgba(85, 105, 255, 0.08)'
              : undefined
          }}
        >
          {getInitials(notif.title)}
        </Avatar>
        {notif.status === 'PENDING' && (
          <FiberManualRecordIcon
            sx={{
              position: 'absolute',
              bottom: 2,
              right: 2,
              color: '#ff5f45',
              fontSize: 12,
              border: `1px solid #fff`,
              borderRadius: '50%',
              background: '#fff'
            }}
            aria-label="New notification"
          />
        )}
      </Box>
      <Stack spacing={0.4} flex={1} minWidth={0}>
        <Typography
          component="div"
          sx={{
            fontWeight: notif.status === 'PENDING' ? 700 : 500,
            fontSize: fontSize, // Responsive font size
            lineHeight: 1.3,
            color: notif.status === 'PENDING' ? '#1a1a1a' : 'text.primary',
            letterSpacing: '-.01em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {notif.title}
        </Typography>
        <Typography
          sx={{
            fontSize: fontSize -1, // Responsive font size
            color: notif.status === 'PENDING' ? '#5c5c67' : 'text.secondary',
            fontWeight: 400,
            opacity: 0.95,
            lineHeight: 1.33,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {notif.description}
        </Typography>
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          mt={.1}
        >
          <Typography variant="caption" sx={{ color: '#9e9eae', fontSize: fontSize - 2, fontWeight: 500 }}>
            {formatDate(notif.leaveDate)}
          </Typography>
          {notif.status === 'PENDING' && (
            <Typography
              variant="caption"
              sx={{
                ml: 1,
                px: 0.8,
                borderRadius: 6,
                fontWeight: 600,
                fontSize: fontSize - 3,
                color: '#fff',
                background: 'linear-gradient(93deg,#ff5f45 55%,#ffb041 100%)',
                letterSpacing: '0.01em'
              }}>
              New
            </Typography>
          )}
        </Box>
      </Stack>
    </ListItem>
  );

  return (
    <>
      <Tooltip arrow title="Notifications">
        <Box
          className="header-notification-btn"
          onClick={() => {
            setOpen(true);
            setViewAll(false);
          }}
          ref={ref}
          tabIndex={0}
          sx={{
            outline: 'none',
            cursor: 'pointer',
            borderRadius: '50%',
            p: 0.3,
            '&:focus': {
              boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}`
            }
          }}
        >
          <AppleBadge
            className="notification-badge"
            badgeContent={
              notifications.filter((n) => n.status === 'PENDING').length
            }
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            overlap="circular"
          >
            <NotificationsNoneRoundedIcon
              className="icon-header-color"
              fontSize="small"
              sx={{
                fontSize: { xs: 18, sm: 19, md: 20, lg: 22, xl: 24 }
              }}
            />
          </AppleBadge>
        </Box>
      </Tooltip>
      <Popover
        className="header-popover"
        style={{ marginTop: '32px', marginLeft: '-10px' }}
        anchorEl={ref.current}
        onClose={() => setOpen(false)}
        open={isOpen}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow:
              '0 10px 22px 0 rgba(45,53,102,0.09), 0 1px 7px 0 rgba(55,59,97,0.05)',
            p: 0,
            width: popoverWidth,
            maxWidth: popoverWidth,
            minWidth: { xs: '80vw', sm: 260, md: 300, lg: 340, xl: 370, xxl: 400 },
            maxHeight: popoverMaxHeight,
            overflow: 'auto',
            background: 'transparent'
          }
        }}
        TransitionComponent={Fade}
      >
        <AppleCard
          sx={{
            width: popoverWidth,
            minWidth: { xs: '80vw', sm: 260, md: 300, lg: 340, xl: 370, xxl: 400 },
            maxWidth: popoverWidth,
            p: 0
          }}
        >
          <Box
            sx={{
              p: { xs: 1.2, sm: 1.3, md: 1.4, lg: 1.5, xl: 1.7 },
              pb: { xs: 0.7, sm: 0.8, md: 0.9, lg: 1.0, xl: 1.2 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Typography
              variant="h6"
              fontWeight={800}
              fontSize={fontSize + 1}
              sx={{
                letterSpacing: '0.02em',
                color: '#1a1a1a',
              }}
            >
              Notifications
            </Typography>
          </Box>
          <Divider />
          <Box sx={{ position: 'relative' }}>
            <List
              sx={{
                p: { xs: 0.6, sm: 0.7, md: 0.8, lg: 0.9, xl: 1.1 },
                pt: { xs: 0.2, sm: 0.3, md: 0.4, lg: 0.5, xl: 0.6 },
                pb: { xs: 0.2, sm: 0.3, md: 0.4, lg: 0.5, xl: 0.6 },
                maxHeight: viewAll
                  ? { xs: 130, sm: 200, md: 240, lg: 300, xl: 360 }
                  : 'none',
                overflowY: viewAll ? 'auto' : 'unset',
                minWidth: 0,
                width: '100%',
                boxSizing: 'border-box'
              }}
            >
              {loading ? (
                <ListItem sx={{ justifyContent: 'center', minHeight: 40 }}>
                  <CircularProgress
                    size={20}
                    thickness={4.2}
                    sx={{ color: '#5569ff' }}
                  />
                </ListItem>
              ) : notifications.length === 0 ? (
                <ListItem sx={{ justifyContent: 'center', minHeight: 40 }}>
                  <Typography fontSize={fontSize - 1} color="#8e8e9d" fontWeight={500}>
                    No notifications.
                  </Typography>
                </ListItem>
              ) : (
                visibleNotifications.map(renderNotificationItem)
              )}
            </List>
            {notifications.length > 0 && (
              <Box
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  left: 0,
                  width: '100%',
                  bgcolor: '#fff',
                  zIndex: 2,
                  borderTop: '1px solid #f0f0f0',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 0.7,
                  py: { xs: 0.5, sm: 0.6, md: 0.7, lg: 0.8, xl: 0.9 },
                  px: { xs: 1, sm: 1.1, md: 1.2, lg: 1.3, xl: 1.5 },
                  background:
                    'linear-gradient(0deg, #fff 90%, rgba(255,255,255,0.7) 100%)'
                }}
              >
                {notifications.length > MAX_VISIBLE && (
                  <>
                    {!viewAll ? (
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{
                          textTransform: 'none',
                          fontSize: fontSize - 1,
                          fontWeight: 700,
                          color: '#5569ff',
                          borderColor: '#5569ff',
                          borderRadius: 4,
                          px: { xs: 1.2, sm: 1.3, md: 1.4, lg: 1.5, xl: 1.6 },
                          py: { xs: 0.3, sm: 0.4, md: 0.5, lg: 0.5, xl: 0.6 },
                          bgcolor: 'rgba(85,105,255,0.05)',
                          '&:hover': {
                            backgroundColor: 'rgba(85, 105, 255, 0.11)',
                            borderColor: '#5569ff'
                          }
                        }}
                        onClick={async (e) => {
                          e.preventDefault();
                          setViewAll(true);
                        }}
                      >
                        View All
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{
                          textTransform: 'none',
                          fontSize: fontSize - 1,
                          fontWeight: 700,
                          color: '#5569ff',
                          borderColor: '#5569ff',
                          borderRadius: 4,
                          px: { xs: 1.2, sm: 1.3, md: 1.4, lg: 1.5, xl: 1.6 },
                          py: { xs: 0.3, sm: 0.4, md: 0.5, lg: 0.5, xl: 0.6 },
                          bgcolor: 'rgba(85,105,255,0.04)',
                          '&:hover': {
                            backgroundColor: 'rgba(85, 105, 255, 0.10)',
                            borderColor: '#5569ff'
                          }
                        }}
                        onClick={async (e) => {
                          e.preventDefault();
                          setViewAll(false);
                        }}
                      >
                        Show Less
                      </Button>
                    )}
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        borderColor: 'rgba(0, 0, 0, 0.07)',
                        height: '18px'
                      }}
                    />
                  </>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    textTransform: 'none',
                    fontSize: fontSize - 1,
                    fontWeight: 700,
                    color: '#ff5f45',
                    borderColor: '#ff5f45',
                    borderRadius: 4,
                    px: { xs: 1.2, sm: 1.3, md: 1.4, lg: 1.5, xl: 1.6 },
                    py: { xs: 0.3, sm: 0.4, md: 0.5, lg: 0.5, xl: 0.6 },
                    background: 'rgba(255,95,69,0.04)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,95,69,0.10)',
                      borderColor: '#ff5f45'
                    }
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNotificationClear();
                  }}
                >
                  Clear
                </Button>
              </Box>
            )}
          </Box>
        </AppleCard>
      </Popover>
      {/* Apple-style dialog for notification details */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        fullWidth
        maxWidth={isXl ? 'sm' : 'xs'}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 10px 30px 0 rgba(31,38,135,0.11)',
            background: alpha('#f7f7ff', 0.96),
            backdropFilter: 'blur(16px) saturate(1.6)',
            width: detailDialogWidth,
            maxWidth: detailDialogWidth
          }
        }}
      >
        {/* Responsive header: avatar, title, close icon */}
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'flex-start', md: 'flex-start', lg: 'center', xl: 'center' },
            gap: { xs: 1, sm: 1.2, md: 1.3, lg: 1.5, xl: 1.8 },
            px: { xs: 1.5, sm: 2, md: 2.1, lg: 2.3, xl: 2.5 },
            pt: { xs: 1.2, sm: 1.3, md: 1.4, lg: 1.5, xl: 1.7 },
            pb: { xs: 1, sm: 1.1, md: 1.15, lg: 1.2, xl: 1.25 },
            position: 'relative'
          }}
        >
          <Avatar
            sx={{
              width: { xs: 26, sm: 28, md: 30, lg: 34, xl: 38 },
              height: { xs: 26, sm: 28, md: 30, lg: 34, xl: 38 },
              bgcolor: '#fff',
              color: '#5569ff',
              border: '1px solid #e6e6f8',
              boxShadow: '0 1px 6px 0 rgba(85, 105, 255, 0.07)',
              fontWeight: 700,
              flexShrink: 0,
              mt: { xs: 0.5, sm: 0.5, md: 0.5, lg: 0, xl: 0 },
              fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem', lg: '1rem', xl: '1.1rem' }
            }}
          >
            {getInitials(selectedNotification?.title || 'N')}
          </Avatar>
          <Box 
            sx={{ 
              flex: 1, 
              minWidth: 0, 
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: { xs: 'flex-start', sm: 'flex-start', md: 'flex-start', lg: 'center', xl: 'center' }
            }}
          >
            <Typography
              sx={{
                fontWeight: selectedNotification?.status === 'PENDING' ? 800 : 600,
                fontSize: { xs: fontSize + 1, sm: fontSize + 1, md: fontSize + 1, lg: fontSize + 2, xl: fontSize + 3 },
                color: '#22223a',
                letterSpacing: '-.01em',
                lineHeight: { xs: 1.22, sm: 1.22, md: 1.22, lg: 1.3, xl: 1.35 },
                wordBreak: 'break-word',
                pr: { xs: 3.5, sm: 4, md: 4.5, lg: 5, xl: 6 } // leave space for close icon
              }}
            >
              {selectedNotification?.title}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: '#8e8e9d',
                fontWeight: 500,
                mt: { xs: 0.2, sm: 0.2, md: 0.3, lg: 0.2, xl: 0.2 },
                fontSize: { xs: fontSize - 2, sm: fontSize - 2, md: fontSize - 2, lg: fontSize - 1, xl: fontSize },
                display: 'block'
              }}
            >
              {selectedNotification?.leaveDate
                ? `Received: ${formatDate(selectedNotification.leaveDate)}`
                : ''}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={() => setDetailOpen(false)}
            sx={{
              position: 'absolute',
              right: { xs: 6, sm: 12, md: 16, lg: 18, xl: 20 },
              top: { xs: 8, sm: 10, md: 12, lg: 14, xl: 16 },
              color: '#bdbdbd',
              p: { xs: 0.5, sm: 0.5, md: 0.6, lg: 0.7, xl: 0.8 },
              zIndex: 2,
              background: 'transparent',
              '&:hover': {
                background: alpha(theme.palette.action.active, 0.07)
              }
            }}
            size="small"
          >
            <CloseIcon sx={{ fontSize: { xs: 20, sm: 22, md: 22, lg: 24, xl: 26 } }} />
          </IconButton>
        </Box>
        <DialogContent
          sx={{
            pt: 0.4,
            px: { xs: 1.5, sm: 1.7, md: 1.9, lg: 2.1, xl: 2.4 },
            pb: { xs: 1.0, sm: 1.2, md: 1.3, lg: 1.5, xl: 1.6 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start'
          }}
        >
          <Box
            sx={{
              bgcolor: alpha('#fff', 0.8),
              p: { xs: 1.0, sm: 1.2, md: 1.3, lg: 1.4, xl: 1.7 },
              borderRadius: 2,
              boxShadow: 1,
              width: '100%',
              minHeight: 48,
              mt: 0.8
            }}
          >
            <Typography
              sx={{
                fontSize: fontSize,
                color: '#262626',
                fontWeight: 500,
                whiteSpace: 'pre-line'
              }}
            >
              {selectedNotification?.description}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HeaderNotifications;